import React, { useState, useRef } from 'react';
import { Plus, Edit3, Trash2, GripVertical, Download } from 'lucide-react';
import * as XLSX from 'xlsx';

const CurriculumBuilder = () => {
  const [programName, setProgramName] = useState('');
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState({
    1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 7: [], 8: []
  });
  const [showCourseForm, setShowCourseForm] = useState(false);
  const [newCourse, setNewCourse] = useState({ name: '', credits: '', type: 'A', hours: '1:1', nucleus: 'Común Institucional' });
  const [editingCourse, setEditingCourse] = useState(null);
  const [draggedCourse, setDraggedCourse] = useState(null);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [showExportForm, setShowExportForm] = useState(false);
  const [exportData, setExportData] = useState({ participants: '', emails: '' });
  const [formationLevel, setFormationLevel] = useState('Técnico');
  const [showLevelChangeConfirm, setShowLevelChangeConfirm] = useState(false);
  const [pendingLevel, setPendingLevel] = useState('');
  const courseIdCounter = useRef(0);
  
  const COURSE_TYPES = ['A', 'B', 'C', 'D'];
  const HOURS_OPTIONS = ['1:2', '2:1', '3:1', '1:1', '1:3'];
  const FORMATION_NUCLEI = [
    { name: 'Común Institucional', color: 'yellow' },
    { name: 'Común por Campo de Conocimiento', color: 'green' },
    { name: 'Básico Profesional', color: 'red' },
    { name: 'Formación Profesional Específica', color: 'blue' }
  ];
  const FORMATION_LEVELS = {
    'Técnico': { semesters: 4, type: 'pregrado' },
    'Tecnólogo': { semesters: 6, type: 'pregrado' },
    'Universitario': { semesters: 8, type: 'pregrado' },
    'Especialización': { semesters: 2, type: 'posgrado', minCredits: 21, maxCredits: 27 },
    'Maestría': { semesters: 3, type: 'posgrado', minCredits: 39, maxCredits: 42 }
  };

  const MAX_CREDITS = { 1: 15, 2: 18, 3: 18, 4: 18, 5: 18, 6: 18, 7: 18, 8: 15 };

  // Helper functions
  const getActiveSemesters = () => FORMATION_LEVELS[formationLevel].semesters;
  const getFormationType = () => FORMATION_LEVELS[formationLevel].type;
  const getCreditLimits = () => FORMATION_LEVELS[formationLevel];
  const isPostgraduate = () => getFormationType() === 'posgrado';

  const getMaxCreditsForSemester = (semesterNum) => {
    if (isPostgraduate()) {
      // Para posgrados, distribución más flexible
      const limits = getCreditLimits();
      return Math.ceil(limits.maxCredits / getActiveSemesters()) + 3; // Un poco de flexibilidad
    }
    return MAX_CREDITS[semesterNum] || 18;
  };

  const validateTotalCredits = () => {
    if (!isPostgraduate()) return { valid: true, message: '' };
    
    const total = getTotalCredits();
    const limits = getCreditLimits();
    
    if (total < limits.minCredits) {
      return { 
        valid: false, 
        message: `El programa debe tener al menos ${limits.minCredits} créditos (actual: ${total})` 
      };
    }
    if (total > limits.maxCredits) {
      return { 
        valid: false, 
        message: `El programa no puede exceder ${limits.maxCredits} créditos (actual: ${total})` 
      };
    }
    return { valid: true, message: '' };
  };

  const getCreditsByType = () => {
    const allCourses = Object.values(semesters).flat();
    const summary = { A: 0, B: 0, C: 0, D: 0 };
    allCourses.forEach(course => {
      summary[course.type] += course.credits;
    });
    return summary;
  };

  const getCreditsByNucleus = () => {
    const allCourses = Object.values(semesters).flat();
    const summary = {};
    FORMATION_NUCLEI.forEach(nucleus => {
      summary[nucleus.name] = 0;
    });
    allCourses.forEach(course => {
      if (course.nucleus) {
        summary[course.nucleus] += course.credits;
      }
    });
    return summary;
  };

  const getSemesterCredits = (semesterNum) => {
    return semesters[semesterNum].reduce((total, course) => total + course.credits, 0);
  };

  const canAddCourse = (semesterNum, credits) => {
    return getSemesterCredits(semesterNum) + credits <= getMaxCreditsForSemester(semesterNum);
  };

  const getTotalCredits = () => {
    return Object.values(semesters).flat().reduce((total, course) => total + course.credits, 0);
  };

  const getNucleusColor = (nucleusName) => {
    const nucleus = FORMATION_NUCLEI.find(n => n.name === nucleusName);
    return nucleus ? nucleus.color : 'gray';
  };

  const getNucleusColorClasses = (nucleusName) => {
    const color = getNucleusColor(nucleusName);
    switch(color) {
      case 'yellow': return 'bg-yellow-400';
      case 'green': return 'bg-green-400';
      case 'red': return 'bg-red-400';
      case 'blue': return 'bg-blue-400';
      default: return 'bg-gray-400';
    }
  };

  const handleLevelChange = (newLevel) => {
    const currentType = getFormationType();
    const newType = FORMATION_LEVELS[newLevel].type;
    const hasContent = getTotalCredits() > 0;
    
    // Si hay contenido y se cambia entre pregrado y posgrado, confirmar
    if (hasContent && currentType !== newType) {
      setPendingLevel(newLevel);
      setShowLevelChangeConfirm(true);
    } else {
      setFormationLevel(newLevel);
    }
  };

  const confirmLevelChange = () => {
    setFormationLevel(pendingLevel);
    // Limpiar todo el contenido
    setCourses([]);
    setSemesters({ 1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 7: [], 8: [] });
    setIsConfirmed(false);
    setShowLevelChangeConfirm(false);
    setPendingLevel('');
  };

  const cancelLevelChange = () => {
    setShowLevelChangeConfirm(false);
    setPendingLevel('');
  };

  const handleCreateCourse = () => {
    if (newCourse.name.trim() && newCourse.credits && parseInt(newCourse.credits) > 0) {
      const course = {
        id: ++courseIdCounter.current,
        name: newCourse.name.trim(),
        credits: parseInt(newCourse.credits),
        type: isConfirmed ? newCourse.type : 'A',
        hours: isConfirmed ? newCourse.hours : '1:1',
        nucleus: newCourse.nucleus
      };
      
      if (editingCourse) {
        // Actualizar curso existente
        setCourses(courses.map(c => c.id === editingCourse.id ? course : c));
        // Actualizar en semestres si está asignado
        const newSemesters = { ...semesters };
        Object.keys(newSemesters).forEach(sem => {
          newSemesters[sem] = newSemesters[sem].map(c => c.id === editingCourse.id ? course : c);
        });
        setSemesters(newSemesters);
        setEditingCourse(null);
      } else {
        setCourses([...courses, course]);
      }
      
      setNewCourse({ name: '', credits: '', type: 'A', hours: '1:1', nucleus: 'Común Institucional' });
      setShowCourseForm(false);
    }
  };

  const handleEditCourse = (course) => {
    setEditingCourse(course);
    setNewCourse({ 
      name: course.name, 
      credits: course.credits.toString(),
      type: course.type || 'A',
      hours: course.hours || '1:1',
      nucleus: course.nucleus || 'Común Institucional'
    });
    setShowCourseForm(true);
  };

  const handleDeleteCourse = (courseId) => {
    setCourses(courses.filter(c => c.id !== courseId));
    // Remover de semestres
    const newSemesters = { ...semesters };
    Object.keys(newSemesters).forEach(sem => {
      newSemesters[sem] = newSemesters[sem].filter(c => c.id !== courseId);
    });
    setSemesters(newSemesters);
  };

  const handleDragStart = (e, course) => {
    setDraggedCourse(course);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetSemester) => {
    e.preventDefault();
    if (!draggedCourse) return;

    const semesterNum = parseInt(targetSemester);
    
    // Verificar si puede agregarse al semestre
    if (!canAddCourse(semesterNum, draggedCourse.credits)) {
      const maxCredits = getMaxCreditsForSemester(semesterNum);
      alert(`No se puede agregar el curso. Excedería el límite de ${maxCredits} créditos para el semestre ${semesterNum}.`);
      setDraggedCourse(null);
      return;
    }

    // Remover curso de donde estaba
    const newSemesters = { ...semesters };
    Object.keys(newSemesters).forEach(sem => {
      newSemesters[sem] = newSemesters[sem].filter(c => c.id !== draggedCourse.id);
    });

    // Agregar al nuevo semestre
    newSemesters[semesterNum] = [...newSemesters[semesterNum], draggedCourse];
    setSemesters(newSemesters);

    // Remover de cursos disponibles si estaba allí
    setCourses(courses.filter(c => c.id !== draggedCourse.id));
    setDraggedCourse(null);
  };

  const moveCourseToAvailable = (course) => {
    // Remover de semestres
    const newSemesters = { ...semesters };
    Object.keys(newSemesters).forEach(sem => {
      newSemesters[sem] = newSemesters[sem].filter(c => c.id !== course.id);
    });
    setSemesters(newSemesters);
    
    // Agregar a cursos disponibles
    setCourses([...courses, course]);
  };

  const generateExcel = () => {
    // Preparar datos para Excel
    const worksheetData = [];
    
    // Encabezado del reporte
    worksheetData.push(['MALLA CURRICULAR - ' + (programName || 'PROGRAMA ACADÉMICO')]);
    worksheetData.push(['Fecha de creación:', new Date().toLocaleDateString('es-ES')]);
    worksheetData.push(['Participantes:', exportData.participants]);
    worksheetData.push(['Correos electrónicos:', exportData.emails]);
    worksheetData.push([]);

    // Resumen general
    worksheetData.push(['RESUMEN GENERAL']);
    worksheetData.push(['Nivel de formación:', `${formationLevel} (${getActiveSemesters()} semestres)`]);
    worksheetData.push(['Total de créditos:', getTotalCredits()]);
    worksheetData.push([]);

    // Resumen por tipo
    if (isConfirmed) {
      const creditsByType = getCreditsByType();
      worksheetData.push(['DISTRIBUCIÓN POR TIPO DE CURSO']);
      worksheetData.push(['Tipo', 'Créditos', 'Porcentaje']);
      COURSE_TYPES.forEach(type => {
        const credits = creditsByType[type];
        const percentage = getTotalCredits() > 0 ? ((credits / getTotalCredits()) * 100).toFixed(1) + '%' : '0.0%';
        worksheetData.push([`Tipo ${type}`, credits, percentage]);
      });
      worksheetData.push([]);
    }

    // Resumen por núcleo
    if (isConfirmed) {
      const creditsByNucleus = getCreditsByNucleus();
      worksheetData.push(['DISTRIBUCIÓN POR NÚCLEO DE FORMACIÓN']);
      worksheetData.push(['Núcleo', 'Créditos', 'Porcentaje']);
      FORMATION_NUCLEI.forEach(nucleus => {
        const credits = creditsByNucleus[nucleus.name];
        const percentage = getTotalCredits() > 0 ? ((credits / getTotalCredits()) * 100).toFixed(1) + '%' : '0.0%';
        worksheetData.push([nucleus.name, credits, percentage]);
      });
      worksheetData.push([]);
    }

    // Malla por semestres
    worksheetData.push(['MALLA CURRICULAR POR SEMESTRES']);
    worksheetData.push([]);

    for (let semester = 1; semester <= getActiveSemesters(); semester++) {
      worksheetData.push([`SEMESTRE ${semester}`]);
      
      if (isConfirmed) {
        worksheetData.push(['Curso', 'Créditos', 'Núcleo', 'Tipo', 'Relación de Horas']);
      } else {
        worksheetData.push(['Curso', 'Créditos', 'Núcleo']);
      }

      const semesterCourses = semesters[semester];
      if (semesterCourses.length > 0) {
        semesterCourses.forEach(course => {
          if (isConfirmed) {
            worksheetData.push([course.name, course.credits, course.nucleus, `Tipo ${course.type}`, course.hours]);
          } else {
            worksheetData.push([course.name, course.credits, course.nucleus]);
          }
        });
      } else {
        worksheetData.push(['(Sin cursos asignados)', '', '']);
      }

      const semesterCredits = getSemesterCredits(semester);
      const maxCredits = getMaxCreditsForSemester(semester);
      worksheetData.push(['TOTAL SEMESTRE:', semesterCredits, `(Máximo: ${maxCredits})`]);
      worksheetData.push([]);
    }

    // Lista completa de cursos
    worksheetData.push(['LISTADO COMPLETO DE CURSOS']);
    if (isConfirmed) {
      worksheetData.push(['Semestre', 'Curso', 'Créditos', 'Núcleo', 'Tipo', 'Relación de Horas']);
    } else {
      worksheetData.push(['Semestre', 'Curso', 'Créditos', 'Núcleo']);
    }

    for (let semester = 1; semester <= getActiveSemesters(); semester++) {
      semesters[semester].forEach(course => {
        if (isConfirmed) {
          worksheetData.push([semester, course.name, course.credits, course.nucleus, `Tipo ${course.type}`, course.hours]);
        } else {
          worksheetData.push([semester, course.name, course.credits, course.nucleus]);
        }
      });
    }

    // Crear libro de Excel
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);
    
    // Ajustar ancho de columnas
    const colWidths = [
      { wch: 30 }, // Columna A
      { wch: 15 }, // Columna B
      { wch: 15 }, // Columna C
      { wch: 20 }, // Columna D
      { wch: 20 }  // Columna E
    ];
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Malla Curricular');

    // Descargar archivo
    const fileName = `Malla_Curricular_${formationLevel}_${programName.replace(/\s+/g, '_') || 'Programa'}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);

    // Cerrar modal
    setShowExportForm(false);
    setExportData({ participants: '', emails: '' });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Constructor de Planes de Estudio</h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Programa:</label>
              <input
                type="text"
                value={programName}
                onChange={(e) => setProgramName(e.target.value)}
                placeholder="Ingrese el nombre del programa académico"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nivel de Formación:</label>
              <select
                value={formationLevel}
                onChange={(e) => handleLevelChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <optgroup label="Pregrado">
                  {Object.keys(FORMATION_LEVELS).filter(level => FORMATION_LEVELS[level].type === 'pregrado').map(level => (
                    <option key={level} value={level}>
                      {level} ({FORMATION_LEVELS[level].semesters} semestres)
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Posgrado">
                  {Object.keys(FORMATION_LEVELS).filter(level => FORMATION_LEVELS[level].type === 'posgrado').map(level => {
                    const levelData = FORMATION_LEVELS[level];
                    return (
                      <option key={level} value={level}>
                        {level} ({levelData.semesters} semestres, {levelData.minCredits}-{levelData.maxCredits} créditos)
                      </option>
                    );
                  })}
                </optgroup>
              </select>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              <div>Total de créditos del programa: <span className="font-semibold text-lg text-blue-600">{getTotalCredits()}</span></div>
              {programName.trim() === '' && getTotalCredits() > 0 && (
                <div className="text-red-600 font-medium text-xs mt-1">
                  ⚠️ Debe ingresar el nombre del programa para confirmar la malla
                </div>
              )}
              {isPostgraduate() && (
                <div className="mt-1">
                  <span className="text-xs">
                    Rango requerido: {getCreditLimits().minCredits} - {getCreditLimits().maxCredits} créditos
                  </span>
                  {(() => {
                    const validation = validateTotalCredits();
                    return !validation.valid && (
                      <div className="text-red-600 font-medium text-xs mt-1">
                        {validation.message}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
            <div className="flex gap-3">
              {!isConfirmed && (
                <button
                  onClick={() => setIsConfirmed(true)}
                  disabled={getTotalCredits() === 0 || programName.trim() === ''}
                  className={`px-4 py-2 rounded-md flex items-center gap-2 ${
                    getTotalCredits() === 0 || programName.trim() === ''
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                  title={
                    programName.trim() === '' 
                      ? 'Debe ingresar el nombre del programa' 
                      : getTotalCredits() === 0 
                      ? 'Debe agregar al menos un curso' 
                      : 'Confirmar malla curricular'
                  }
                >
                  ✓ Confirmar Malla
                </button>
              )}
              {isConfirmed && (
                <div className="flex items-center gap-3">
                  <span className="text-green-600 font-medium">✓ Malla Confirmada</span>
                  <button
                    onClick={() => setIsConfirmed(false)}
                    className="bg-yellow-600 text-white px-3 py-2 rounded-md hover:bg-yellow-700 text-sm"
                  >
                    Editar Estructura
                  </button>
                  <button
                    onClick={() => setShowExportForm(true)}
                    disabled={getTotalCredits() === 0}
                    className={`px-4 py-2 rounded-md flex items-center gap-2 ${
                      getTotalCredits() === 0 
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                  >
                    <Download size={16} />
                    Generar Propuesta
                  </button>
                </div>
              )}
              <button
                onClick={() => {
                  setShowCourseForm(true);
                  setEditingCourse(null);
                  setNewCourse({ name: '', credits: '', type: 'A', hours: '1:1', nucleus: 'Común Institucional' });
                }}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
              >
                <Plus size={16} />
                Crear Curso
              </button>
            </div>
          </div>
        </div>

        {/* Formulario de creación de cursos */}
        {showCourseForm && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6 border-2 border-blue-200">
            <h3 className="text-lg font-semibold mb-4">
              {editingCourse ? 'Editar Curso' : 'Crear Nuevo Curso'}
            </h3>
            
            {!isConfirmed ? (
              // Modo básico: nombre, créditos y núcleo
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Curso</label>
                  <input
                    type="text"
                    value={newCourse.name}
                    onChange={(e) => setNewCourse({...newCourse, name: e.target.value})}
                    placeholder="Ej: Cálculo Diferencial"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="w-32">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Créditos</label>
                  <select
                    value={newCourse.credits}
                    onChange={(e) => setNewCourse({...newCourse, credits: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Seleccionar</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                  </select>
                </div>
                <div className="w-60">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Núcleo de Formación</label>
                  <select
                    value={newCourse.nucleus}
                    onChange={(e) => setNewCourse({...newCourse, nucleus: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {FORMATION_NUCLEI.map(nucleus => (
                      <option key={nucleus.name} value={nucleus.name}>{nucleus.name}</option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={handleCreateCourse}
                  className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700"
                >
                  {editingCourse ? 'Actualizar' : 'Crear'}
                </button>
                <button
                  onClick={() => {
                    setShowCourseForm(false);
                    setEditingCourse(null);
                    setNewCourse({ name: '', credits: '', type: 'A', hours: '1:1', nucleus: 'Común Institucional' });
                  }}
                  className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
                >
                  Cancelar
                </button>
              </div>
            ) : (
              // Modo avanzado: todos los campos
              <div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-4">
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Curso</label>
                    <input
                      type="text"
                      value={newCourse.name}
                      onChange={(e) => setNewCourse({...newCourse, name: e.target.value})}
                      placeholder="Ej: Cálculo Diferencial"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Créditos</label>
                    <select
                      value={newCourse.credits}
                      onChange={(e) => setNewCourse({...newCourse, credits: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Seleccionar</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Núcleo de Formación</label>
                    <select
                      value={newCourse.nucleus}
                      onChange={(e) => setNewCourse({...newCourse, nucleus: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {FORMATION_NUCLEI.map(nucleus => (
                        <option key={nucleus.name} value={nucleus.name}>{nucleus.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Curso</label>
                    <div className="flex gap-1">
                      {COURSE_TYPES.map(type => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setNewCourse({...newCourse, type})}
                          className={`px-3 py-2 text-sm rounded border ${
                            newCourse.type === type
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Relación de Horas</label>
                    <select
                      value={newCourse.hours}
                      onChange={(e) => setNewCourse({...newCourse, hours: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {HOURS_OPTIONS.map(hours => (
                        <option key={hours} value={hours}>{hours}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex gap-4">
                  <button
                    onClick={handleCreateCourse}
                    className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700"
                  >
                    {editingCourse ? 'Actualizar' : 'Crear'}
                  </button>
                  <button
                    onClick={() => {
                      setShowCourseForm(false);
                      setEditingCourse(null);
                      setNewCourse({ name: '', credits: '', type: 'A', hours: '1:1', nucleus: 'Común Institucional' });
                    }}
                    className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Cursos disponibles */}
        {courses.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4">Cursos Disponibles</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {courses.map(course => (
                <div
                  key={course.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, course)}
                  className="bg-blue-50 border border-blue-200 rounded-lg p-3 cursor-move hover:shadow-md transition-shadow group relative overflow-hidden"
                >
                  {/* Franja diagonal del núcleo */}
                  <div className={`absolute top-0 right-0 w-0 h-0 border-l-[20px] border-l-transparent border-t-[20px] ${getNucleusColorClasses(course.nucleus)}`}></div>
                  
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <GripVertical size={16} className="text-gray-400" />
                      <div className="flex-1">
                        <div className="font-medium text-sm">{course.name}</div>
                        <div className="text-xs text-gray-600 space-y-1">
                          <div>{course.credits} créditos</div>
                          {isConfirmed && (
                            <div className="flex gap-2">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                course.type === 'A' ? 'bg-red-100 text-red-800' :
                                course.type === 'B' ? 'bg-green-100 text-green-800' :
                                course.type === 'C' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-purple-100 text-purple-800'
                              }`}>
                                Tipo {course.type}
                              </span>
                              <span className="text-xs text-gray-500">{course.hours}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleEditCourse(course)}
                        className="p-1 hover:bg-blue-200 rounded"
                      >
                        <Edit3 size={12} />
                      </button>
                      <button
                        onClick={() => handleDeleteCourse(course.id)}
                        className="p-1 hover:bg-red-200 rounded text-red-600"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Malla curricular */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h3 className="text-lg font-semibold mb-6">Malla Curricular</h3>
          <div className="overflow-x-auto">
            <div className="flex gap-4 min-w-max pb-4">
              {Object.keys(semesters).slice(0, getActiveSemesters()).map(semester => {
                const semesterNum = parseInt(semester);
                const currentCredits = getSemesterCredits(semesterNum);
                const maxCredits = getMaxCreditsForSemester(semesterNum);
                const isOverLimit = currentCredits > maxCredits;
                
                return (
                  <div
                    key={semester}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, semester)}
                    className={`border-2 border-dashed rounded-lg p-4 min-h-80 w-64 flex-shrink-0 transition-colors ${
                      draggedCourse && canAddCourse(semesterNum, draggedCourse.credits)
                        ? 'border-green-400 bg-green-50'
                        : draggedCourse
                        ? 'border-red-400 bg-red-50'
                        : 'border-gray-300 bg-gray-50'
                    }`}
                  >
                    <div className="text-center mb-3">
                      <h4 className="font-semibold text-gray-900">Semestre {semester}</h4>
                      <div className={`text-sm ${isOverLimit ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                        {currentCredits} / {getMaxCreditsForSemester(semesterNum)} créditos
                        {isOverLimit && (
                          <div className="text-xs text-red-500 mt-1">¡Límite excedido!</div>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      {semesters[semester].map(course => (
                        <div
                          key={course.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, course)}
                          className="bg-white border border-gray-200 rounded p-2 cursor-move hover:shadow-sm group relative overflow-hidden"
                        >
                          {/* Franja diagonal del núcleo */}
                          <div className={`absolute top-0 right-0 w-0 h-0 border-l-[15px] border-l-transparent border-t-[15px] ${getNucleusColorClasses(course.nucleus)}`}></div>
                          
                          <div className="flex justify-between items-start">
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">{course.name}</div>
                              <div className="text-xs text-gray-500 space-y-1">
                                <div>{course.credits} créditos</div>
                                {isConfirmed && (
                                  <div className="flex gap-1">
                                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                      course.type === 'A' ? 'bg-red-100 text-red-800' :
                                      course.type === 'B' ? 'bg-green-100 text-green-800' :
                                      course.type === 'C' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-purple-100 text-purple-800'
                                    }`}>
                                      {course.type}
                                    </span>
                                    <span className="text-xs text-gray-500">{course.hours}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity ml-1">
                              <button
                                onClick={() => handleEditCourse(course)}
                                className="p-1 hover:bg-gray-200 rounded"
                              >
                                <Edit3 size={10} />
                              </button>
                              <button
                                onClick={() => moveCourseToAvailable(course)}
                                className="p-1 hover:bg-yellow-200 rounded text-yellow-600"
                                title="Mover a disponibles"
                              >
                                ↑
                              </button>
                              <button
                                onClick={() => handleDeleteCourse(course.id)}
                                className="p-1 hover:bg-red-200 rounded text-red-600"
                                title="Eliminar del programa"
                              >
                                <Trash2 size={10} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {semesters[semester].length === 0 && (
                        <div className="text-center text-gray-400 text-sm py-8">
                          Arrastra cursos aquí
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Cuadro resumen */}
        {isConfirmed && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4">Resumen por Tipo de Curso</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {COURSE_TYPES.map(type => {
                const credits = getCreditsByType()[type];
                const percentage = getTotalCredits() > 0 ? ((credits / getTotalCredits()) * 100).toFixed(1) : '0.0';
                const colorClass = 
                  type === 'A' ? 'border-red-200 bg-red-50' :
                  type === 'B' ? 'border-green-200 bg-green-50' :
                  type === 'C' ? 'border-yellow-200 bg-yellow-50' :
                  'border-purple-200 bg-purple-50';
                const textColor = 
                  type === 'A' ? 'text-red-800' :
                  type === 'B' ? 'text-green-800' :
                  type === 'C' ? 'text-yellow-800' :
                  'text-purple-800';
                
                return (
                  <div key={type} className={`border rounded-lg p-4 ${colorClass}`}>
                    <div className="text-center">
                      <div className={`text-2xl font-bold ${textColor}`}>Tipo {type}</div>
                      <div className={`text-lg font-semibold ${textColor}`}>{credits} créditos</div>
                      <div className={`text-sm ${textColor}`}>{percentage}%</div>
                    </div>
                  </div>
                );
              })}
            </div>

            <h3 className="text-lg font-semibold mb-4">Resumen por Núcleo de Formación</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {FORMATION_NUCLEI.map(nucleus => {
                const credits = getCreditsByNucleus()[nucleus.name];
                const percentage = getTotalCredits() > 0 ? ((credits / getTotalCredits()) * 100).toFixed(1) : '0.0';
                const colorClasses = {
                  yellow: 'border-yellow-200 bg-yellow-50 text-yellow-800',
                  green: 'border-green-200 bg-green-50 text-green-800',
                  red: 'border-red-200 bg-red-50 text-red-800',
                  blue: 'border-blue-200 bg-blue-50 text-blue-800'
                };
                
                return (
                  <div key={nucleus.name} className={`border rounded-lg p-4 ${colorClasses[nucleus.color]}`}>
                    <div className="text-center">
                      <div className="text-sm font-medium mb-1">{nucleus.name}</div>
                      <div className="text-xl font-bold">{credits} créditos</div>
                      <div className="text-sm">{percentage}%</div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="mt-4 text-center text-gray-600 font-medium">
              Total del programa: {getTotalCredits()} créditos ({formationLevel} - {getActiveSemesters()} semestres)
              {isPostgraduate() && (
                <div className="text-sm mt-1">
                  Rango: {getCreditLimits().minCredits} - {getCreditLimits().maxCredits} créditos
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal de confirmación de cambio de nivel */}
        {showLevelChangeConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold mb-4 text-red-600">⚠️ Confirmar Cambio de Nivel</h3>
              <p className="text-gray-700 mb-4">
                Está cambiando de <strong>{getFormationType()}</strong> a <strong>{FORMATION_LEVELS[pendingLevel].type}</strong>.
              </p>
              <p className="text-gray-700 mb-6">
                <strong>¡Atención!</strong> Este cambio eliminará todos los cursos y la malla curricular actual. 
                ¿Está seguro de que desea continuar?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={confirmLevelChange}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
                >
                  Sí, cambiar y borrar contenido
                </button>
                <button
                  onClick={cancelLevelChange}
                  className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal para exportar */}
        {showExportForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-semibold mb-4">Generar Propuesta de Malla Curricular</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombres de los Participantes
                  </label>
                  <textarea
                    value={exportData.participants}
                    onChange={(e) => setExportData({...exportData, participants: e.target.value})}
                    placeholder="Ej: Dr. Juan Pérez, Dra. María González, Prof. Carlos López"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Correos Electrónicos
                  </label>
                  <textarea
                    value={exportData.emails}
                    onChange={(e) => setExportData({...exportData, emails: e.target.value})}
                    placeholder="Ej: juan.perez@universidad.edu, maria.gonzalez@universidad.edu"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={generateExcel}
                  className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 flex items-center justify-center gap-2"
                >
                  <Download size={16} />
                  Generar Excel
                </button>
                <button
                  onClick={() => {
                    setShowExportForm(false);
                    setExportData({ participants: '', emails: '' });
                  }}
                  className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CurriculumBuilder;
